import { OvForm, OvFormProvider } from '@components'
import { LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { CheckCircle, Logout } from '@mui/icons-material'
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { MY_COMPANY_DEFAULT_VALUES } from 'src/constants/forms/my-company/my-company.defaultvalues.constants'
import { MyCompanyFormGeneral } from 'src/partials/index.partials'

export const OnboardingView = observer(() => {
  const { user, company, auth, ui } = useStore()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const form = useForm({
    defaultValues: {
      profile: { ...user.current_user },
      company: MY_COMPANY_DEFAULT_VALUES,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const getPageTitle = useMemo(() => {
    return `Welkom, ${user.current_user_name}!`
  }, [user.current_user_name])

  const handleFinish = async (data: any) => {
    const { profile, company: companyData } = data

    try {
      // Validate required company fields (only name is required)
      if (!companyData.name) {
        ui.handleToaster('Bedrijfsnaam is verplicht', { type: 'error' })
        return
      }

      // Update user profile
      await user.update(profile)

      // Retrieve billing_interval from localStorage (set during registration)
      // If not set, user chose free tier - don't send billing_interval
      let billingInterval = localStorage.getItem('billing_interval') || null

      // Backwards compatibility: convert old 'monthly'/'yearly' to 'month'/'year'
      if (billingInterval === 'monthly') billingInterval = 'month'
      if (billingInterval === 'yearly') billingInterval = 'year'

      // Create company - only include billing_interval if user selected a plan
      const companyPayload = billingInterval
        ? { ...companyData, billing_interval: billingInterval }
        : companyData

      const response = await company.create(companyPayload)

      if (response) {
        // Clean up localStorage
        localStorage.removeItem('billing_interval')

        // Check if we got a checkout URL (for Stripe payment setup)
        if (response.checkout_url) {
          // Redirect to Stripe Checkout for payment setup
          window.location.href = response.checkout_url
          return
        }

        // If no checkout URL, proceed to dashboard
        ui.handleToaster('Welkom bij Outvoice! Je account is klaar voor gebruik.', { type: 'success' })

        // Extract company data from response
        const responseCompanyData = response.company || response

        // Reset form to mark it as pristine (not dirty)
        form.reset(
          {
            profile,
            company: responseCompanyData,
          },
          { keepValues: true }
        )

        // Force a re-check of company status before navigating
        await company.getMyCompany()
        navigate(URL_PATHS.DASHBOARD)
      }
    } catch (e: any) {
      ui.handleToaster(e?.message || 'Er is iets misgegaan bij het voltooien van de onboarding', { type: 'error' })
    }
  }

  const onFormSubmit = () => {
    form.handleSubmit(handleFinish)()
  }

  // Check if user already has a company and redirect them
  // TEMPORARY: Disabled to allow testing onboarding
  // useEffect(() => {
  //   const checkExistingCompany = async () => {
  //     try {
  //       await company.getMyCompany()
  //       if (company.has_company && company.current_company?.name) {
  //         // User already has a company, redirect to dashboard
  //         navigate(URL_PATHS.DASHBOARD, { replace: true })
  //       }
  //     } catch (e) {
  //       // User doesn't have a company, stay on onboarding
  //       console.log('No company found, continuing with onboarding')
  //     }
  //   }
  //   checkExistingCompany()
  // }, [])

  return (
    <OvFormProvider
      form={form}
      onSubmit={onFormSubmit}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          width: '100%',
          maxWidth: '100%',
          margin: 0,
          padding: 0,
        }}>
        {/* Header */}
        <Paper
          elevation={1}
          sx={{ position: 'sticky', top: 0, zIndex: 99, borderRadius: 0, width: '100%' }}>
          <Container
            maxWidth="xl"
            sx={{ py: isMobile ? 1.5 : 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600 }}>
                {getPageTitle}
              </Typography>
            </Box>
          </Container>
        </Paper>

        {/* Main content */}
        <Container
          maxWidth="xl"
          sx={{ py: 4 }}>
          <OvForm>
            <Box sx={{ minHeight: 'calc(100vh - 300px)', pb: 11 }}>
              <Paper
                elevation={2}
                sx={{ p: isMobile ? 3 : 5, maxWidth: 900, mx: 'auto' }}>
                <Stack spacing={3}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      color="primary">
                      Je bedrijfsgegevens
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary">
                      Vul je bedrijfsgegevens in om facturen te kunnen versturen
                    </Typography>
                  </Box>
                  <MyCompanyFormGeneral />
                </Stack>
              </Paper>
            </Box>

            {/* Footer */}
            <Paper
              elevation={8}
              sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderRadius: 0, zIndex: 100 }}>
              <Container
                maxWidth="xl"
                sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={TOOLTIPS.LOGOUT_ONBOARDING}>
                    <Button
                      color="inherit"
                      onClick={() => auth.handleLogOut()}
                      startIcon={<Logout />}>
                      {LABELS.LOGOUT}
                    </Button>
                  </Tooltip>
                  <Box sx={{ flex: '1 1 auto' }}></Box>
                  <Tooltip title={TOOLTIPS.COMPLETE_ONBOARDING}>
                    <Button
                      type="submit"
                      variant="contained"
                      size={isMobile ? 'small' : 'large'}
                      color="primary"
                      endIcon={<CheckCircle />}>
                      Account voltooien
                    </Button>
                  </Tooltip>
                </Box>
              </Container>
            </Paper>
          </OvForm>
        </Container>
      </Box>
    </OvFormProvider>
  )
})
